// Utilitários para gerenciar o localStorage com segurança

interface StorageItem<T = unknown> {
  value: T
  timestamp: number
  expiresIn?: number // em milissegundos
}

class StorageManager {
  private static instance: StorageManager
  
  private constructor() {}
  
  public static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager()
    }
    return StorageManager.instance
  }

  // Verificar se localStorage está disponível
  private isStorageAvailable(): boolean {
    try {
      const test = '__storage_test__'
      localStorage.setItem(test, test)
      localStorage.removeItem(test)
      return true
    } catch {
      return false
    }
  }

  // Salvar item no localStorage
  public setItem<T>(key: string, value: T, expiresIn?: number): boolean {
    if (!this.isStorageAvailable()) {
      console.warn('localStorage não está disponível')
      return false
    }

    try {
      const item: StorageItem<T> = {
        value,
        timestamp: Date.now(),
        expiresIn
      }
      
      localStorage.setItem(key, JSON.stringify(item))
      return true
    } catch (error) {
      console.error('Erro ao salvar no localStorage:', error)
      return false
    }
  }

  // Obter item do localStorage
  public getItem<T>(key: string): T | null {
    if (!this.isStorageAvailable()) {
      return null
    }

    try {
      const stored = localStorage.getItem(key)
      if (!stored) return null

      // Tentar fazer parse do JSON
      let item: StorageItem<T>
      try {
        item = JSON.parse(stored)
      } catch (parseError) {
        console.warn(`⚠️ Erro ao fazer parse do item '${key}', removendo item corrompido`)
        this.removeItem(key)
        return null
      }
      
      // Verificar se expirou
      if (item.expiresIn && Date.now() - item.timestamp > item.expiresIn) {
        this.removeItem(key)
        return null
      }

      return item.value
    } catch (error) {
      console.error('Erro ao ler do localStorage:', error)
      this.removeItem(key)
      return null
    }
  }

  // Remover item do localStorage
  public removeItem(key: string): boolean {
    if (!this.isStorageAvailable()) {
      return false
    }

    try {
      localStorage.removeItem(key)
      return true
    } catch (error) {
      console.error('Erro ao remover do localStorage:', error)
      return false
    }
  }

  // Limpar localStorage
  public clear(): boolean {
    if (!this.isStorageAvailable()) {
      return false
    }

    try {
      localStorage.clear()
      return true
    } catch (error) {
      console.error('Erro ao limpar localStorage:', error)
      return false
    }
  }

  // Verificar se item existe e não expirou
  public hasValidItem(key: string): boolean {
    return this.getItem(key) !== null
  }

  // Obter timestamp de quando o item foi criado
  public getItemTimestamp(key: string): number | null {
    if (!this.isStorageAvailable()) {
      return null
    }

    try {
      const stored = localStorage.getItem(key)
      if (!stored) return null

      const item: StorageItem = JSON.parse(stored)
      return item.timestamp
    } catch {
      return null
    }
  }
}

// Instância singleton
export const storage = StorageManager.getInstance()

// Chaves específicas para o app
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'tander_auth_token',
  SUBSCRIPTION_CACHE: 'tander_subscription_cache',
  USER_PROFILE_CACHE: 'tander_user_profile_cache',
  AUTO_REDIRECT_DISABLED: 'tander_auto_redirect_disabled',
  LAST_LOGIN: 'tander_last_login',
  VERIFICATION_PENDING: 'tander_verification_pending'
} as const

export default storage
